import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Input, InputLabel, MenuItem, Stack, TextField } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogPlanLlmPrompt } from '../../api/DialogPlanLlmPromptApi';
import { DialogPromptMatchGame, MatchEnum } from '../../api/DialogPromptApi';
import { IVocabularyWord } from '../../api/VocabularyWordApi';
import AppBarContext from '../../context/AppBarContext';
import { useVocabularyWordListPinyin } from '../../hook/useVocabularyWordListPinyin';
import { arrayBufferToBase64 } from '../../util/base64Utils';

interface DialogPlanLlmPromptMatchGameGeneratedProps {
    dialogPrompt?: DialogPromptMatchGame; // if llmPrompt was generated
    onSubmit?: (updatedDialogPlanLlmPrompt: DialogPlanLlmPrompt, setIsSaving: React.Dispatch<React.SetStateAction<boolean>>) => void;
    onDelete?: (dialogPlanLlmPromptId: number) => void;

    isRegenerating?: boolean;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
}

const DialogPlanLlmPromptMatchGameGenerated: React.FC<DialogPlanLlmPromptMatchGameGeneratedProps> = ({ dialogPrompt }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    // const [vocabularyWordList, setVocabularyWordList] = React.useState<IVocabularyWord[]>([]);
    const {
        vocabularyWordList,
        getVocabularyWordPinyinMap,
        getVocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordList,
        setVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    } = useVocabularyWordListPinyin({ history, location, setErrorMessage })

    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
    const [wordIndexToDelete, setWordIndexToDelete] = React.useState<number | null>(null);

    const [leftMatch, setLeftMatch] = React.useState(MatchEnum.FirstWordCharacter);
    const [rightMatch, setRightMatch] = React.useState(MatchEnum.FirstWordPinyin);

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptMatchGame) => {
        if ('vocabulary_word_list' in dialogPrompt) {
            setVocabularyWordFields(dialogPrompt.vocabulary_word_list);
        } else {
            setVocabularyWordFields([]);
        }
        setLeftMatch(dialogPrompt.left_match);
        setRightMatch(dialogPrompt.right_match);
        // setVocabularyWordList(dialogPrompt.vocabulary_word_list);
    }, [setVocabularyWordFields, setLeftMatch, setRightMatch])

    React.useEffect(() => {
        if (dialogPrompt) {
            clearVocabularyWordErrors()
            setDialogPromptFields(dialogPrompt);
        }
    }, [clearVocabularyWordErrors, dialogPrompt, setDialogPromptFields])


    const handleLeftMatchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value as MatchEnum;
        setLeftMatch(value);
        if (dialogPrompt) dialogPrompt.left_match = value;
    };

    const handleRightMatchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value as MatchEnum;
        setRightMatch(value);
        if (dialogPrompt) dialogPrompt.right_match = value;
    };

    const handleAddVocabularyWord = () => {
        const newVocabularyWord: IVocabularyWord = {
            id: 0,
            word: '',
            image: '',
            image_url: '',
            audio_sentence: '',
            audio_sentence_id: 0,
            dialog_prompt_id: 0,
            teacher_english_prompt: '',
            teacher_chinese_prompt: '',
            teacher_chinese_prompt_sentence_id: 0,
            student_response_prompt: '<Answer/>',
            student_response_prompt_sentence_id: 0,
            word_pinyin: [],
            audio_sentence_pinyin: [],
            teacher_chinese_prompt_pinyin: [],
            student_response_prompt_pinyin: [],
            word_info_text: null,
            english_text: null
        };
        // if (vocabularyWordList) {
        //     setVocabularyWordList([...vocabularyWordList, newVocabularyWord]);
        // } else {
        //     setVocabularyWordList([newVocabularyWord]);
        // }
        setVocabularyWordList(prev => [...(Array.isArray(prev) ? prev : []), newVocabularyWord]);
        if (dialogPrompt) {
            dialogPrompt.vocabulary_word_list = [
                ...dialogPrompt.vocabulary_word_list,
                newVocabularyWord
            ];
        }
    };

    // const handleVocabularyWordChange = (
    //     index: number,
    //     field: string,
    //     value: string
    // ) => {
    //     setVocabularyWordList((prevWords) =>
    //         prevWords.map((word, i) => {
    //             if (i === index) {
    //                 return { ...word, [field]: value };
    //             }
    //             return word;
    //         })
    //     );
    //     if (dialogPrompt) dialogPrompt.vocabulary_word_list[index].word = value
    // };
    React.useEffect(() => {
        if (dialogPrompt && vocabularyWordList)
            dialogPrompt.vocabulary_word_list = vocabularyWordList
    }, [vocabularyWordList, dialogPrompt])

    const handleVocabularyWordImageUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // Code to handle the reader result
                let base64String: string;
                const result = reader.result;
                if (typeof result === 'string') {
                    base64String = result;
                } else if (result instanceof ArrayBuffer) {
                    base64String = arrayBufferToBase64(result)
                } else {
                    base64String = ""
                }
                // setVocabularyWordList((prevWords) =>
                //     prevWords.map((word: IVocabularyWord, i) => {
                //         if (i == index) {
                //             return { ...word, image: base64String }
                //         }
                //         return word;
                //     })
                // )
                setVocabularyWordValue(index, 'image', base64String)
                if (dialogPrompt) dialogPrompt.vocabulary_word_list[index].image = base64String
            };

            reader.readAsDataURL(file);
        }
    };

    const handleDeleteVocabularyWord = (index: number) => {
        setWordIndexToDelete(index);
        setDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (wordIndexToDelete !== null) {
            setVocabularyWordList(prevList => prevList.filter((word, index) => index !== wordIndexToDelete));
            if (dialogPrompt) dialogPrompt.vocabulary_word_list.splice(wordIndexToDelete, 1);
        }
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setWordIndexToDelete(null);
        setDeleteConfirmationOpen(false);
    };

    return (<>
        <Stack spacing={4} sx={{ width: '100%', margin: '0 auto', pt: 2 }}>
            <InputLabel id="left-match-label">Left Match</InputLabel>
            <TextField
                select
                value={leftMatch}
                onChange={handleLeftMatchChange}
                fullWidth
            >
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>

            <InputLabel id="right-match-label">Right Match</InputLabel>
            <TextField
                select
                value={rightMatch}
                onChange={handleRightMatchChange}
                fullWidth
            >
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>
            <h3>Answer List</h3>
        </Stack>


        {vocabularyWordList && vocabularyWordList.map((word, index) => {
            // Assuming word.image contains the ArrayBuffer
            return (

                <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pb: 4 }} key={index}>
                    <TextField
                        label="First Chinese Word, Phrase or Sentence"
                        value={word.word}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'word', e.target.value)
                        }
                    />
                    <TextField
                        label="First Chinese Word Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'word_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'word_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'word_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'word_pinyin')}
                    />
                    <TextField
                        label="Second Chinese Word, Phrase or Sentence (with audio)"
                        value={word.audio_sentence}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'audio_sentence', e.target.value)
                        }
                    />
                    <TextField
                        label="Second Chinese Word Pinyin"
                        value={getVocabularyWordPinyinMap(index, 'audio_sentence_pinyin')}
                        onChange={(e) => handleVocabularyWordPinyinChange(index, 'audio_sentence_pinyin', e.target.value)}
                        error={getVocabularyWordErrorMap(index, 'audio_sentence_pinyin') != null}
                        helperText={getVocabularyWordErrorMap(index, 'audio_sentence_pinyin')}
                    />
                    <TextField
                        label="English Word, Phrase or Sentence"
                        value={word.english_text}
                        onChange={(e) =>
                            handleVocabularyWordTextChange(index, 'english_text', e.target.value)
                        }
                    />
                    <Box>
                        <InputLabel htmlFor="image-upload">Answer Image</InputLabel>
                        <Input
                            type="file"
                            id="image-upload"
                            inputProps={{ accept: "image/*" }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleVocabularyWordImageUpload(index, e)}
                        />
                    </Box>
                    {/* Display the image or a placeholder with a fixed height of 300px */}
                    <Box
                        sx={{
                            height: '300px',
                            display: 'flex',
                            flexDirection: 'column', // Stack items vertically (image first, button below)
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {word.image && (
                            <img
                                src={word.image}
                                alt="Uploaded"
                                style={{
                                    maxHeight: '200px', maxWidth: '200px',
                                    height: 'auto', width: 'auto',
                                    margin: '0 auto', // Add this line to center the image horizontally
                                    display: 'block', // Add this line to ensure the margin works correctly
                                }}
                            />
                        )}
                        {/* {word.image && word.image != "broken" && (
                            TODO add delete image button? needed in original DialgoPromptMatchGameForm also
                        )} */}
                    </Box>

                    <Box sx={{ marginTop: 2 }}>
                        <Button variant="outlined" onClick={() => handleDeleteVocabularyWord(index)}>
                            Delete Answer
                        </Button>
                    </Box>
                </Stack>
            )
        })}
        <Button variant="outlined" onClick={handleAddVocabularyWord}>
            Add Answer
        </Button>
        <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete this answer {wordIndexToDelete !== null && (<>{vocabularyWordList[wordIndexToDelete].word}</>)} ?
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelDelete} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary">
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};

export default DialogPlanLlmPromptMatchGameGenerated;
